import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import Icon from '../components/Icon';
import Avatar from '../components/Avatar';
import CreditBar from '../components/CreditBar';
import GoalSuggestSheet from '../components/GoalSuggestSheet';
import FoodPreferencesFields, { DIETS } from '../components/FoodPreferencesFields';
import { PROVIDER_LABELS } from '../components/SocialButtons';
import { Group, NumberField, Row, SectionTitle } from '../components/SettingsRows';
import { useAiCredits } from '../lib/credits';
import Sheet from '../components/Sheet';
import HomeScreenCard from '../components/HomeScreenCard';
import { SignOutButton } from '../components/AuthLinks';
import DeleteAccount from '../components/DeleteAccount';
import LaunchModeGroup from '../components/LaunchModeGroup';
import { FeedbackToggle } from '../components/FeedbackButton';
import { ProBadge } from '../components/ProSheet';
import { SettingsSkeleton } from '../components/Skeletons';
import { ApplyFromChoice, askFrom } from '../components/Targets';
import { LOCALE } from '../lib/format';
import { useApp, useAppPath } from '../lib/AppContext';
import { AnnouncementBanner, NewBadge, useAnnouncementAnchor } from '../components/Announcements';

// ---------- Profile summary and "Gói & lượt AI" (both open their own page) ----------

function ProfileSummary({ form }) {
  const { profile } = useApp();
  const path = useAppPath();
  const summary = [
    form.weight_kg !== '' && form.weight_kg != null && `${Number(form.weight_kg).toLocaleString(LOCALE)} kg`,
    form.height_cm && `${form.height_cm} cm`,
    form.training_days_per_week !== '' && form.training_days_per_week != null && `${form.training_days_per_week} buổi tập/tuần`,
  ].filter(Boolean).join(' · ');
  return (
    <Link to={path('/profile')} className="flex items-center gap-4 rounded-[20px] bg-base-100 p-4">
      <Avatar name={form.name} url={profile?.avatar_url} size={56} />
      <span className="min-w-0 flex-1">
        <span className="block truncate text-xl font-bold">{form.name || 'Chưa đặt tên'}</span>
        <span className="block text-sm text-muted">{summary || 'Thêm cân nặng, chiều cao và số buổi tập'}</span>
      </span>
      <span className="shrink-0 text-sm font-semibold text-brand-text">Sửa hồ sơ</span>
    </Link>
  );
}

// ---------- Kiểu ăn and Món không ăn được (the AI's menus and dish suggestions keep to them) ----------

function FoodGroup({ data, onSaved }) {
  const { api } = useApp();
  const [open, setOpen] = useState(false);
  const [food, setFood] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const diet = DIETS.find((d) => d.id === data.diet_style)?.label || 'Ăn mọi thứ';
  const avoid = data.avoid_foods || [];
  const edit = () => { setFood({ diet: data.diet_style || 'all', avoid }); setError(null); setOpen(true); };
  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      onSaved(await api.patch('/profile', { diet_style: food.diet, avoid_foods: food.avoid }));
      setOpen(false);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <SectionTitle>Ăn uống</SectionTitle>
      <Group>
        <button type="button" onClick={edit} className="flex min-h-14 w-full items-center justify-between gap-3 py-2 text-left">
          <span className="flex min-w-0 items-center gap-3">
            <Icon name="bowl" className="size-5 shrink-0" />
            <span className="min-w-0">
              <span className="block text-[15px] font-medium">Kiểu ăn và món không ăn được</span>
              <span className="block truncate text-xs text-muted">{avoid.length ? `${diet} · Không ăn: ${avoid.join(', ')}` : diet}</span>
            </span>
          </span>
          <Icon name="chevronRight" className="size-5 shrink-0 text-muted" />
        </button>
      </Group>
      <Sheet open={open} onClose={() => setOpen(false)} title="Ăn uống">
        {food && (
          <div className="flex flex-col gap-4">
            <p className="text-sm text-muted">Thực đơn tuần, đổi món và Đặt ngoài sẽ chỉ gợi ý món hợp với bạn.</p>
            <FoodPreferencesFields diet={food.diet} avoid={food.avoid} onChange={setFood} />
            {error && <p role="alert" className="text-sm font-medium text-error">{error}</p>}
            <button type="button" className="btn btn-primary h-[54px] w-full rounded-2xl border-0 text-base font-bold" disabled={busy} onClick={save}>
              {busy && <span className="loading loading-spinner loading-sm" />} Lưu
            </button>
          </div>
        )}
      </Sheet>
    </>
  );
}

const PLAN_LINES = { free: 'Gói Miễn phí', trial: 'Pro (dùng thử)', pro: 'Gói Pro', gift: 'Pro do Coach tặng', unlimited: 'Không giới hạn' };

function CreditsCard() {
  const { membership: m } = useApp();
  const path = useAppPath();
  if (!m) return null;
  const c = m.credits;
  // With an ad per request (free, ads on), what's left is the ads still allowed plus credits already earned.
  const left = m.ads?.enabled ? m.ads.left + c.left : c.left;
  const limit = m.ads?.enabled ? m.ads.daily : c.limit;
  return (
    <Link to={path('/credits')} className="flex flex-col gap-3 rounded-[20px] bg-base-100 p-4">
      <span className="flex items-center gap-3">
        <span className="grid size-10 place-items-center rounded-[12px] bg-[#e3f1e9] text-[#17804f]"><Icon name="sparkles" className="size-5" /></span>
        <span className="min-w-0 flex-1">
          <span className="flex items-center gap-1.5 font-bold">Gói & lượt AI {m.pro && <ProBadge />}</span>
          <span className="block text-xs text-muted">{PLAN_LINES[m.tier]}{c.daily ? ` · ${c.daily} lượt mỗi ngày` : m.ads?.enabled ? ` · ${m.ads.daily} lượt mỗi ngày, có quảng cáo` : ''}</span>
        </span>
        <Icon name="chevronRight" className="size-5 text-muted" />
      </span>
      {c.limit != null && (
        <>
          <span className="flex justify-between text-sm">
            <span>Còn <b className="tabular">{left}/{limit}</b> lượt hôm nay</span>
            <span className="text-muted">{m.month ? `Tháng này còn ${m.month.left}/${m.month.limit}` : 'Làm mới lúc 0:00'}</span>
          </span>
          <CreditBar left={left} limit={limit} />
        </>
      )}
    </Link>
  );
}

// ---------- Share link ----------

// Opening a shared link straight in the app needs Universal Links, which need the paid Apple developer
// account; until then the caption would promise something that doesn't happen.
const UNIVERSAL_LINKS = false;

function QrSheet({ url, onClose }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    if (!url) return;
    import('qrcode').then(({ default: QRCode }) => QRCode.toDataURL(url, { width: 560, margin: 1, color: { dark: '#17201b', light: '#ffffff' } }))
      .then(setSrc).catch(() => setSrc(null));
  }, [url]);
  return (
    <Sheet open={!!url} onClose={onClose} title="Mã QR nhật ký">
      {url && (
        <div className="flex flex-col items-center gap-3 text-center">
          {src ? <img src={src} alt={`Mã QR của link ${url}`} className="size-64 rounded-[16px] border border-line" /> : <div className="skeleton size-64 rounded-[16px]" />}
          <p className="text-sm text-muted">Mở camera trên điện thoại khác và quét mã để xem nhật ký.</p>
        </div>
      )}
    </Sheet>
  );
}

// Your log is private until the link is on; a new link or turning it off cuts off the old one.
function ShareGroup() {
  const { api, myShareCode, setMyShareCode, native } = useApp();
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [qr, setQr] = useState(false);
  const [error, setError] = useState(null);
  // In the app, links point at the website (the app itself is served from capacitor://localhost).
  const url = myShareCode ? `${native?.siteUrl || window.location.origin}/${myShareCode}/day` : null;

  const run = async (method, confirmText) => {
    if (confirmText && !window.confirm(confirmText)) return;
    setBusy(true);
    setError(null);
    try {
      setMyShareCode((await api[method]('/share_link')).share_code);
      setCopied(false);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
    } catch {
      window.prompt('Sao chép link này:', url);
    }
  };

  // The phone's share sheet (Zalo, Messages…), or copy where there is none.
  const share = async () => {
    if (!navigator.share) return copy();
    try {
      await navigator.share({ title: 'Nhật ký Macro Mate', url });
    } catch (e) {
      if (e?.name !== 'AbortError') copy();
    }
    return undefined;
  };

  const outline = 'btn h-12 gap-2 whitespace-nowrap rounded-[16px] border-line bg-base-100 px-3 text-[15px] font-bold';
  return (
    <>
      <SectionTitle>Chia sẻ nhật ký</SectionTitle>
      <Group>
        <Row label="Cho xem qua link" hint="Ai có link đều xem được, nhưng không sửa được" htmlFor="share-toggle">
          <input id="share-toggle" type="checkbox" className="toggle toggle-lg border-base-300 bg-base-300 text-white checked:border-primary checked:bg-primary checked:text-white" checked={!!url} disabled={busy}
            onChange={(e) => (e.target.checked ? run('post') : run('del', 'Tắt chia sẻ? Link hiện tại sẽ không mở được nữa.'))} />
        </Row>
        {url && (
          <>
            <div className="flex flex-col gap-3 py-4">
              <button type="button" className="btn btn-primary h-[54px] rounded-[16px] border-0 text-base font-bold" onClick={share}>
                <Icon name="upload" className="size-5" /> Chia sẻ nhật ký
              </button>
              <div className="grid grid-cols-2 gap-3">
                <button type="button" className={outline} onClick={copy}>
                  <Icon name={copied ? 'check' : 'copy'} className="size-5" /> {copied ? 'Đã sao chép' : 'Sao chép link'}
                </button>
                <button type="button" className={outline} onClick={() => setQr(true)}>
                  <Icon name="qr" className="size-5" /> Mã QR
                </button>
              </div>
              {UNIVERSAL_LINKS && (
                <p className="text-center text-xs text-muted">Bạn bè đã cài Macro Mate sẽ mở thẳng trong app, chưa cài thì xem trên web.</p>
              )}
            </div>
            {/* Opens the same pages read-only, exactly as people with the link see them. */}
            <Link to={`/${myShareCode}/day`} className="flex min-h-14 items-center justify-between gap-3">
              <span className="flex items-center gap-3 text-[15px] font-medium"><Icon name="eye" className="size-5" /> Xem như khách</span>
              <Icon name="chevronRight" className="size-5 text-muted" />
            </Link>
            <button type="button" disabled={busy} className="flex min-h-14 w-full items-center gap-3 py-2 text-left"
              onClick={() => run('post', 'Tạo link mới? Link cũ sẽ không mở được nữa.')}>
              <Icon name="refresh" className="size-5 shrink-0" />
              <span>
                <span className="block text-[15px] font-medium">Tạo link mới</span>
                <span className="block text-xs text-muted">Link cũ sẽ ngừng hoạt động</span>
              </span>
            </button>
          </>
        )}
        {error && <p role="alert" className="py-2 text-sm text-error">{error}</p>}
      </Group>
      <QrSheet url={qr ? url : null} onClose={() => setQr(false)} />
    </>
  );
}

// ---------- Sign-in ----------

// "Zalo · số điện thoại": the ways this account signs in, under the row that opens them.
const methodNames = (methods) => methods.map((m) => PROVIDER_LABELS[m] || (m === 'phone' ? 'số điện thoại' : 'email')).join(' · ');

function SecurityRow() {
  const { api } = useApp();
  const path = useAppPath();
  const [methods, setMethods] = useState(null);
  useEffect(() => { api.get('/account/security').then((s) => setMethods(s.sign_in_methods)).catch(() => {}); }, [api]);
  return (
    <>
      <SectionTitle>Tài khoản</SectionTitle>
      <Group>
        <Link to={path('/settings/security')} className="flex min-h-16 items-center justify-between gap-3 py-2">
          <span className="flex min-w-0 items-center gap-3">
            <Icon name="lock" className="size-5 shrink-0" />
            <span className="min-w-0">
              <span className="block text-[15px] font-medium">Đăng nhập và bảo mật</span>
              {methods?.length > 0 && <span className="block truncate text-xs text-muted">{methodNames(methods)}</span>}
            </span>
          </span>
          <Icon name="chevronRight" className="size-5 shrink-0 text-muted" />
        </Link>
      </Group>
    </>
  );
}

// ---------- Coach mode ----------

// "Huấn luyện viên" for students (connect with a coach, what they see) and "Dành cho huấn luyện viên"
// (coach mode: free to turn on); with coach mode on, also "Khi mở app", as in coach mode's Tài khoản.
function CoachGroup() {
  const { api, coach, setCoach } = useApp();
  const path = useAppPath();
  const remindersAnchor = useAnnouncementAnchor('settings_reminders');
  const scheduleAnchor = useAnnouncementAnchor('settings_schedule');
  const myCoachAnchor = useAnnouncementAnchor('settings_my_coach');
  const coachModeAnchor = useAnnouncementAnchor('settings_coach_mode');
  const row = 'flex min-h-14 items-center justify-between gap-3 py-2';
  const [launchError, setLaunchError] = useState(null);
  const setLaunch = (mode) => {
    setLaunchError(null);
    api.patch('/coach', { launch_mode: mode })
      .then((data) => setCoach((c) => ({ ...c, launch_mode: data.launch_mode })))
      .catch((e) => setLaunchError(e.message));
  };
  return (
    <>
      <SectionTitle>Thông báo</SectionTitle>
      <Group>
        <Link ref={remindersAnchor} to={path('/settings/notifications')} className={row}>
          <span className="flex items-center gap-3">
            <Icon name="bell" className="size-5 shrink-0" />
            <span>
              <span className="block text-[15px] font-medium">Nhắc nhở <NewBadge anchor="settings_reminders" /></span>
              <span className="block text-xs text-muted">Nhắc lịch tập, ghi bữa ăn, giờ yên lặng</span>
            </span>
          </span>
          <Icon name="chevronRight" className="size-5 shrink-0 text-muted" />
        </Link>
      </Group>
      <SectionTitle>Huấn luyện viên</SectionTitle>
      <Group>
        <Link ref={scheduleAnchor} to={path('/schedule')} className={row}>
          <span className="flex items-center gap-3">
            <Icon name="calendar" className="size-5 shrink-0" />
            <span>
              <span className="block text-[15px] font-medium">Lịch tập với Coach <NewBadge anchor="settings_schedule" /></span>
              <span className="block text-xs text-muted">Buổi hẹn trong tuần, xin đổi giờ, báo nghỉ</span>
            </span>
          </span>
          <Icon name="chevronRight" className="size-5 shrink-0 text-muted" />
        </Link>
        <Link ref={myCoachAnchor} to={path('/my-coach')} className={row}>
          <span className="flex items-center gap-3">
            <Icon name="user" className="size-5 shrink-0" />
            <span>
              <span className="block text-[15px] font-medium">Huấn luyện viên của tôi <NewBadge anchor="settings_my_coach" /></span>
              <span className="block text-xs text-muted">Kết nối bằng mã HLV, chọn những gì chia sẻ</span>
            </span>
          </span>
          <Icon name="chevronRight" className="size-5 shrink-0 text-muted" />
        </Link>
        <Link ref={coachModeAnchor} to={path(coach ? '/coach' : '/coach/intro')} className={row}>
          <span className="flex items-center gap-3">
            <Icon name="users" className="size-5 shrink-0" />
            <span>
              <span className="block text-[15px] font-medium">Dành cho huấn luyện viên <NewBadge anchor="settings_coach_mode" /></span>
              <span className="block text-xs text-muted">{coach ? `Đang bật · mã ${coach.code}` : 'Theo dõi học viên, tặng Pro · miễn phí'}</span>
            </span>
          </span>
          <Icon name="chevronRight" className="size-5 shrink-0 text-muted" />
        </Link>
      </Group>
      {coach && <LaunchModeGroup value={coach.launch_mode} onSelect={setLaunch} />}
      {launchError && <p role="alert" className="px-1 pt-2 text-sm font-medium text-error">{launchError}</p>}
    </>
  );
}

// ---------- Help, admin ----------

// "Hiện nút góp ý" (feedback goes through the floating button now) and, for admins, the admin panel.
function HelpGroup() {
  const { admin, native } = useApp();
  const row = 'flex min-h-14 items-center justify-between gap-3 text-[15px] font-medium';
  return (
    <>
      <SectionTitle>Hỗ trợ</SectionTitle>
      <Group>
        <FeedbackToggle />
        {/* For the account an Admin is linked to. The admin panel is a website page with its own login (wider,
            for a computer); in the app this opens it in the browser. */}
        {admin && (
          <a href={native ? `${native.siteUrl}/admin` : '/admin'} target={native ? '_blank' : undefined} rel="noreferrer" className={row}>
            <span className="flex items-center gap-3"><Icon name="user" className="size-5" /> Trang quản trị</span>
            <Icon name="chevronRight" className="size-5 text-muted" />
          </a>
        )}
      </Group>
    </>
  );
}

// ---------- Delete account ----------

// ---------- Page ----------

// Only the daily targets are edited here; the profile has its own page ("Sửa hồ sơ").
const GOAL_FIELDS = ['calorie_goal', 'protein_goal', 'workout_protein_goal'];

export default function SettingsPage() {
  const { api, setGoals, native, setProfile } = useApp();
  const path = useAppPath();
  const navigate = useNavigate();
  const location = useLocation();
  const calorieRef = useRef(null);
  const [profileData, setProfileData] = useState(null);
  const [form, setForm] = useState(null);
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  // "Áp dụng mục tiêu mới từ khi nào?", asked when meals were already logged today.
  const [askingFrom, setAskingFrom] = useState(false);
  const [from, setFrom] = useState('today');
  // Opening the AI suggestion checks credits first; out of credits offers to type the targets instead.
  // (After the sheet has closed: closing hands focus back to the button that opened it.)
  const suggestCredits = useAiCredits(1, { label: 'Tự nhập mục tiêu', onClick: () => setTimeout(() => calorieRef.current?.focus(), 150) });
  const historyAnchor = useAnnouncementAnchor('settings_target_history');

  const load = (data) => {
    setProfileData(data);
    setForm(Object.fromEntries(GOAL_FIELDS.map((key) => [key, data[key] ?? ''])));
    setGoals({ calories: data.calorie_goal, protein_g: data.protein_goal, workout_protein_g: data.workout_protein_goal });
    setProfile((p) => ({ ...p, name: data.name, avatar_url: data.avatar_url, needsName: data.needs_name }));
  };

  useEffect(() => { api.get('/profile').then(load); }, [api]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = (event) => {
    event.preventDefault();
    if (askFrom(profileData.today)) {
      setFrom('today');
      setAskingFrom(true);
      return;
    }
    submit('today');
  };

  const submit = async (targetsFrom) => {
    setAskingFrom(false);
    setBusy(true);
    setStatus(null);
    try {
      load(await api.patch('/profile', { ...form, targets_from: targetsFrom }));
      setStatus({ ok: true, text: targetsFrom === 'tomorrow' ? 'Đã lưu, mục tiêu mới áp dụng từ ngày mai' : 'Đã lưu' });
    } catch (e) {
      setStatus({ ok: false, text: e.message });
    } finally {
      setBusy(false);
    }
  };

  // The suggestion was applied on the server; reload so the fields and the app's goals match it.
  const applied = async (_result, targetsFrom) => {
    setSuggesting(false);
    load(await api.get('/profile'));
    const when = targetsFrom === 'tomorrow' ? ' từ ngày mai' : '';
    setStatus({ ok: true, text: `Đã áp dụng gợi ý${when}. Bạn vẫn sửa được từng con số.` });
  };

  // Back to wherever the account button was tapped from; the home page when opened directly.
  const back = () => (location.key !== 'default' ? navigate(-1) : navigate(path('/')));

  const header = (
    <div className="pt-safe flex items-center gap-1 pt-4">
      <button type="button" className="btn btn-circle btn-ghost -ml-2 size-11" aria-label="Quay lại" onClick={back}>
        <Icon name="chevronLeft" className="size-6" />
      </button>
      <h1 className="text-[22px] font-bold">Tài khoản và cài đặt</h1>
    </div>
  );

  if (!form) return <div className="flex flex-col gap-4">{header}<SettingsSkeleton /></div>;
  const set = (key) => (value) => setForm((f) => ({ ...f, [key]: value }));
  // Targets the coach set: shown locked, changed only by the coach.
  const lockedBy = profileData.targets_locked_by;
  const dirty = GOAL_FIELDS.some((key) => String(form[key]) !== String(profileData[key] ?? ''));

  return (
    <div className="flex flex-col gap-1">
      {header}
      <div className="flex flex-col gap-3 pt-3">
        <AnnouncementBanner screen="settings" />
        <ProfileSummary form={profileData} />
        <CreditsCard />
      </div>

      <form onSubmit={save}>
        <div className="flex items-center justify-between gap-2">
          <SectionTitle>Mục tiêu mỗi ngày</SectionTitle>
          {!lockedBy && <button type="button" className="mt-2 inline-flex h-9 items-center gap-1.5 rounded-full bg-[#e3f1e9] px-3 text-xs font-bold text-[#17804f]"
            onClick={() => !suggestCredits.blocked(() => setSuggesting(true)) && setSuggesting(true)}>
            <Icon name="sparkles" className="size-4" /> Gợi ý bằng AI
          </button>}
        </div>
        <Group>
          <Row label="Calo" hint="Kcal đốt khi tập được cộng thêm" htmlFor="calories">
            <NumberField id="calories" inputRef={calorieRef} unit="kcal" locked={!!lockedBy} value={form.calorie_goal} onChange={set('calorie_goal')} />
          </Row>
          <Row label="Đạm ngày nghỉ" hint="Mức tối thiểu cần đạt" htmlFor="protein">
            <NumberField id="protein" unit="g" accent="text-protein" locked={!!lockedBy} value={form.protein_goal} onChange={set('protein_goal')} />
          </Row>
          <Row label="Đạm ngày tập" hint="Dùng khi hôm đó có ghi tập luyện" htmlFor="protein-workout">
            <NumberField id="protein-workout" unit="g" accent="text-protein" locked={!!lockedBy}
              value={lockedBy && form.workout_protein_goal === '' ? form.protein_goal : form.workout_protein_goal} onChange={set('workout_protein_goal')} />
          </Row>
          <div className="flex flex-col gap-2 py-4">
            {status && <p role="status" className={`text-center text-sm font-medium ${status.ok ? 'text-brand-text' : 'text-error'}`}>{status.text}</p>}
            {lockedBy ? (
              <p className="flex gap-2 rounded-[14px] bg-[#fdf3d7] p-3 text-sm text-[#5c4300]">
                <Icon name="lock" className="mt-0.5 size-4 shrink-0" />
                Mục tiêu do Coach {lockedBy.name} đặt. Chỉ Coach sửa được, muốn đổi hãy trao đổi với Coach.
              </p>
            ) : (
              <button type="submit" className="btn btn-primary h-[54px] w-full rounded-2xl border-0 text-base font-bold" disabled={busy || !dirty}>
                {busy && <span className="loading loading-spinner loading-sm" />} Lưu thay đổi
              </button>
            )}
            <Link ref={historyAnchor} to={path('/targets')} className="flex h-11 items-center justify-center gap-1.5 text-sm font-semibold text-brand-text">
              <Icon name="clock" className="size-4" /> Lịch sử mục tiêu <NewBadge anchor="settings_target_history" />
            </Link>
          </div>
        </Group>
      </form>

      <FoodGroup data={profileData} onSaved={load} />
      <SecurityRow />
      <CoachGroup />
      <ShareGroup />
      <HelpGroup />
      {/* Inside the native app it's already on the home screen. */}
      {!native && <div className="pt-4"><HomeScreenCard /></div>}

      <div className="flex flex-col gap-2 pt-6">
        <Group className="px-0">
          <SignOutButton className="h-14 w-full text-[15px] font-semibold text-error" />
        </Group>
        <DeleteAccount />
      </div>

      <Sheet open={askingFrom} onClose={() => setAskingFrom(false)} title="Áp dụng mục tiêu mới từ khi nào?">
        {askingFrom && (
          <div className="flex flex-col gap-4">
            <ApplyFromChoice info={profileData.today} value={from} onChange={setFrom}
              next={Object.fromEntries(GOAL_FIELDS.map((key) => [key, form[key] === '' ? null : Number(form[key])]))} />
            <button type="button" className="btn btn-primary h-[54px] w-full rounded-2xl border-0 text-base font-bold" onClick={() => submit(from)}>
              Xác nhận
            </button>
            <p className="text-center text-xs text-muted">Chỉ hỏi khi bạn đổi mục tiêu sau khi đã ghi bữa ăn trong ngày.</p>
          </div>
        )}
      </Sheet>
      <GoalSuggestSheet open={suggesting} profile={profileData} onApplied={applied} onClose={() => setSuggesting(false)} />
    </div>
  );
}
